/**
 * Uçuşa bağlı Tamamlıyo sigortası — `Insurance/GetPrice` ve `Insurance/CreateOffer` istek
 * gövdelerini ödenmiş/allocate edilmiş sepetten kurar. `runPayment` (flowConfig.ts) ve
 * `AirInsurancePanel` aynı mantığı kullansın diye tek yerde.
 *
 * Kaynak: legacy `TravelFuture/TravelFutureWeb/Helpers/InsuranceHelpers.cs` (alan-alan) +
 * `src/modules/insurance/flowConfig.ts` (mevcut React istek şekli). Canlı doğrulanmadı
 * (Tamamlıyo test kredensiyeli yok). Yurtdışı uçuşta yolcu `citizenNo` sentinel
 * `"00000000000"` olarak gider — legacy `InsuranceHelpers` ile aynı davranış.
 */
import type { ShoppingCart, TravelInsurance } from '../../api/types/shoppingCart';
import type { AirBooking, FlightSegment } from '../../api/types/air';
import type {
  InsuranceCreateOfferRequest,
  InsuranceGetPriceRequest,
} from '../../api/types/insurance';

/** ISO / `YYYY-MM-DD...` → `YYYY-MM-DD`. Geçersizse `''`. */
function toIsoDate(value: string | undefined | null): string {
  if (!value) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

function addOneDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

function airBookingsOf(cart: ShoppingCart | null | undefined): AirBooking[] {
  return (cart?.airBookings ?? []) as AirBooking[];
}

function allSegments(cart: ShoppingCart | null | undefined): FlightSegment[] {
  return airBookingsOf(cart).flatMap((b) => b.flightSegments ?? []);
}

/** Uçuşun yurtdışı olup olmamasına göre Tamamlıyo ürün slug'ı (legacy `InsuranceHelpers`:
 *  `isInternational ? "yurtdisi-seyahat" : "yurtici-seyahat"`). */
export function insuranceProductForCart(cart: ShoppingCart | null | undefined): string {
  const isInternational = airBookingsOf(cart).some((b) => b.isInternational === true);
  return isInternational ? 'yurtdisi-seyahat' : 'yurtici-seyahat';
}

/** Sepetteki ilk sigorta teklifinin `id` (= teklifId) ve `totalFare`'ı. */
export function readCartInsurance(cart: ShoppingCart | null | undefined): {
  teklifId?: string;
  totalFare?: number;
} {
  const first = cart?.travelInsurances?.[0] as TravelInsurance | undefined;
  return { teklifId: first?.id, totalFare: first?.totalFare };
}

/** Sepetteki poliçe PDF'i için teklifId — `id` yoksa `undefined`. */
export function cartInsuranceTeklifId(cart: ShoppingCart | null | undefined): string | undefined {
  return readCartInsurance(cart).teklifId;
}

export interface InsuranceRequestsFromCart {
  getPrice: InsuranceGetPriceRequest;
  createOffer: InsuranceCreateOfferRequest;
}

/**
 * `Insurance/GetPrice` + `Insurance/CreateOffer` gövdeleri. Yolcular `cart.passengers`,
 * tarihler ilk kalkış → son varış (aynı gün ise bitiş +1 gün), ülke son segmentin varış
 * ülkesi / ilk segmentin kalkış havalimanı kodu. `cart.passengers` boşsa `null`.
 */
export function buildInsuranceRequestsFromCart(
  cart: ShoppingCart | null | undefined
): InsuranceRequestsFromCart | null {
  const passengers = cart?.passengers ?? [];
  const segments = allSegments(cart);
  if (passengers.length === 0 || segments.length === 0) return null;

  const first = passengers[0];
  const email = cart?.contact?.email ?? first.email ?? '';
  const gsm = cart?.contact?.phone ?? first.phone ?? '';

  const startDate = toIsoDate(segments[0]?.departureDateTime);
  let endDate = toIsoDate(segments[segments.length - 1]?.arrivalDateTime);
  if (startDate && endDate && startDate === endDate) endDate = addOneDay(endDate);

  const urun = insuranceProductForCart(cart);
  const lastSeg = segments[segments.length - 1] as FlightSegment & { arrivalCountryName?: string };
  const ulkeAdi = lastSeg?.arrivalCountryName ?? undefined;
  const ulkeKoduString = segments[0]?.departureAirportCode ?? undefined;

  const dobOf = (p: (typeof passengers)[number]) => toIsoDate(p.birthDate);

  const getPrice: InsuranceGetPriceRequest = {
    sigortaliSayisi: passengers.length,
    baslangicTarihi: startDate,
    bitisTarihi: endDate,
    urun,
    ulkeAdi,
    customerInfo: passengers.map((p) => ({
      // `ticketType` sağlayıcı-özel (yetişkin/çocuk) — canlı netleşene dek boş, legacy'de "1".
      ticketType: '',
      ad: p.firstName,
      soyad: p.lastName,
      tcKimlikNo: p.citizenNo,
      dogumTarihi: dobOf(p),
      email,
      gsm_no: gsm,
    })),
  };

  const createOffer: InsuranceCreateOfferRequest = {
    sigortaEttiren: { tcKimlikNo: first.citizenNo, dogumTarihi: dobOf(first) },
    sigortali: passengers.map((p) => ({ tcKimlikNo: p.citizenNo, dogumTarihi: dobOf(p) })),
    baslangicTarihi: startDate,
    bitisTarihi: endDate,
    email,
    gsmNo: gsm,
    urun,
    ulkeKoduString,
    shoppingCartId: cart?.id,
  };

  return { getPrice, createOffer };
}
